/**
 * Site Database Codegen
 *
 * Generates a `databases.ts` module from site database definitions.
 * The output imports from `zod/mini` and is intended to be
 * written into the Astro site at `src/generated/databases.ts`.
 *
 * Each database entry contains:
 * - `access`: the access mode (for type-level SDK enforcement)
 * - `schema`: a Zod Mini object schema (for runtime validation)
 * - `fields`: plain metadata per field (type, required, options — for UI rendering)
 *
 * Usage:
 *   import { generateDatabasesModule } from "@repo/services/site-database-codegen"
 *   const source = generateDatabasesModule(databases)
 *   await fs.writeFile("src/generated/databases.ts", source)
 *
 * @module
 */
import { Effect, FileSystem, PlatformError, Schema } from "effect"
import type {
  SiteDatabase as SiteDatabaseType,
  FieldDef as FieldDefType,
} from "@repo/domain/schemas/site-databases"
import {
  SiteDatabase as SiteDatabaseSchema,
  fieldTypeMetadata,
} from "@repo/domain/schemas/site-databases"

// ── Zod Mini Source Mapping ────────────────────────────────────────────

/**
 * Convert a field definition into a Zod Mini expression string.
 *
 * Uses field type metadata from the domain schema to determine
 * max lengths and primitives. Parametric types (select, multiselect)
 * embed their options directly.
 */
function fieldToZodSource(field: FieldDefType): string {
  const meta = fieldTypeMetadata.find((m) => m.kind === field.type)

  // Fallback for unknown types (shouldn't happen if schemas are in sync)
  if (!meta) return "z.unknown()"

  switch (meta.primitive) {
    case "number":
      // `zod/mini` has no `finite` check; `z.number()` is the valid validator.
      return "z.number()"
    case "boolean":
      return "z.boolean()"
    case "string":
      break
  }

  // String-based types — apply type-specific refinements
  switch (field.type) {
    case "email":
      return "z.email()"
    case "url":
      return "z.url()"
    case "date":
      return "z.iso.date()"
    case "datetime":
      return "z.iso.datetime()"
    case "select":
      return `z.enum(${JSON.stringify(field.options ?? [])})`
    case "multiselect":
      return `z.array(z.enum(${JSON.stringify(field.options ?? [])}))`
    default:
      return "maxLength" in meta && typeof meta.maxLength === "number"
        ? `z.string().check(z.maxLength(${meta.maxLength}))`
        : "z.string()"
  }
}

// ── Field Metadata Source ──────────────────────────────────────────────

/**
 * Generate the `fields` metadata object source for a database.
 * This provides plain data (type, required, options) that UI code
 * can use without digging into Zod internals.
 */
function fieldMetaSource(name: string, field: FieldDefType): string {
  const parts: string[] = [
    `type: ${JSON.stringify(field.type)}`,
    `required: ${field.required}`,
  ]
  if (field.options && field.options.length > 0) {
    parts.push(`options: ${JSON.stringify(field.options)}`)
  }
  return `      ${JSON.stringify(name)}: { ${parts.join(", ")} },`
}

// ── Generator ──────────────────────────────────────────────────────────

/**
 * Generate the contents of `databases.ts` from site database definitions.
 *
 * Returns an empty registry (valid, zero databases) when given an empty array.
 */
export function generateDatabasesModule(
  databases: readonly SiteDatabaseType[],
): string {
  const versionComment = databases.length > 0
    ? databases.map((d) => `${d.name}@${d.schemaVersion}`).join(", ")
    : "none"

  const entries = databases.map((db) => {
    const fieldEntries = Object.entries(db.schema.fields)

    // schema: z.object({ ... })
    const schemaFields = fieldEntries.map(([name, field]) => {
      const expr = fieldToZodSource(field)
      const wrapped = field.required ? expr : `z.optional(${expr})`
      return `      ${JSON.stringify(name)}: ${wrapped},`
    })

    // fields: { ... }
    const metaFields = fieldEntries.map(([name, field]) =>
      fieldMetaSource(name, field),
    )

    return [
      `  ${JSON.stringify(db.name)}: {`,
      `    access: ${JSON.stringify(db.accessMode)} as const,`,
      `    schema: z.object({`,
      ...schemaFields,
      `    }),`,
      `    fields: {`,
      ...metaFields,
      `    },`,
      `  },`,
    ].join("\n")
  })

  const lines = [
    `// AUTO-GENERATED by @repo/services/site-database-codegen — do not edit`,
    `// schema_version: ${versionComment}`,
    `//`,
    `// Usage:`,
    `//   await database.insert("contact-form", { name, email })`,
    `//   await database.query("team-members")`,
    `//   await database.query("team-members", { where: { role: { eq: "Manager" } } })`,
    `//   await database.query("team-members", { where: { bio: { contains: "SF" } }, limit: 20 })`,
    `//`,
    `// Operators by field type:`,
    `//   string  → { eq?, ne?, contains? }  (contains is case-insensitive)`,
    `//   number  → { eq?, ne? }`,
    `//   boolean → { eq?, ne? }`,
    `//`,
    `// Rules:`,
    `//   - insert() → write DBs only (compile error on read DBs)`,
    `//   - query()  → read DBs only (compile error on write DBs)`,
    `//   - multiple fields combine with AND; no OR yet`,
    ``,
    `import * as z from "zod/mini"`,
    ``,
    `export const databases = {`,
  ]

  if (entries.length > 0) {
    lines.push(...entries)
  }

  lines.push(
    `} as const`,
    ``,
    `export type Databases = typeof databases`,
    `export type DbName = keyof Databases`,
    ``,
  )

  return lines.join("\n")
}

// ── Project application (write + patch) ─────────────────────────────────

/**
 * The wire shape of a database definition as delivered by the codegen RPCs
 * (`SessionRpc.getSessionSiteDatabases` / `PreTaskOpsRpc.getSiteDatabasesForCodegen`).
 * Structurally a superset-free match of the domain `SiteDatabase`, so it decodes
 * cleanly. Declared locally to keep this package decoupled from `@repo/contracts`.
 */
export interface CodegenDatabaseInput {
  readonly name: string
  readonly description: string
  readonly accessMode: string
  readonly schema: {
    readonly fields: Record<
      string,
      {
        readonly type: string
        readonly required: boolean
        readonly options?: readonly string[] | undefined
      }
    >
  }
  readonly schemaVersion: number
}

/** Project-relative path of the generated registry. */
export const DATABASES_PATH = "src/lib/databases.ts"
/** Project-relative path of the SDK entry the registry wires into. */
export const SDK_PATH = "src/lib/sdk.ts"

const SDK_IMPORT_LINE = 'import { databases } from "./databases"'
const SDK_OLD_INIT = "createSiteSdk({ databases: {} })"
const SDK_NEW_INIT = "createSiteSdk({ databases })"

const decodeSiteDatabase = Schema.decodeUnknownSync(SiteDatabaseSchema)

/**
 * Generate `src/lib/databases.ts` from the workspace's database definitions and
 * ensure `src/lib/sdk.ts` imports it. This is the shared core behind both the
 * VTM executor and the `heysegment codegen` CLI command — the only thing that
 * differs between callers is where `databases` is fetched from.
 *
 * - Decodes the wire shape into the domain schema (validates server data).
 * - Writes `databases.ts` only when its contents change (no needless churn).
 * - Patches `sdk.ts` in place; a no-op when already wired or absent.
 *
 * Returns whether the registry file was (re)written.
 */
export const applyDatabaseCodegen = (input: {
  readonly fs: FileSystem.FileSystem
  readonly projectDir: string
  readonly databases: ReadonlyArray<CodegenDatabaseInput>
}): Effect.Effect<{ readonly wrote: boolean; readonly count: number }, PlatformError.PlatformError> =>
  Effect.gen(function* () {
    const decoded: ReadonlyArray<SiteDatabaseType> = input.databases.map(
      (db) => decodeSiteDatabase(db),
    )
    const source = generateDatabasesModule(decoded)

    const databasesFilePath = `${input.projectDir}/${DATABASES_PATH}`
    const wrote = yield* writeIfChanged(input.fs, databasesFilePath, source)

    yield* patchSdkImport(input.fs, `${input.projectDir}/${SDK_PATH}`)

    return { wrote, count: decoded.length }
  })

/** Write `content` to `path` only if it differs from what's already there. */
const writeIfChanged = (
  fs: FileSystem.FileSystem,
  path: string,
  content: string,
): Effect.Effect<boolean, PlatformError.PlatformError> =>
  Effect.gen(function* () {
    const existing = yield* fs
      .readFileString(path)
      .pipe(
        Effect.catchTag("PlatformError", () =>
          Effect.succeed<string | null>(null),
        ),
      )
    if (existing === content) return false
    yield* fs.writeFileString(path, content)
    return true
  })

/**
 * Ensure `sdk.ts` imports the generated `databases` and feeds it to
 * `createSiteSdk`. Idempotent: returns early when already wired, and is a no-op
 * (with no failure) when the file is absent.
 */
const patchSdkImport = (
  fs: FileSystem.FileSystem,
  sdkFilePath: string,
): Effect.Effect<void, PlatformError.PlatformError> =>
  Effect.gen(function* () {
    const content = yield* fs
      .readFileString(sdkFilePath)
      .pipe(
        Effect.catchTag("PlatformError", () =>
          Effect.succeed<string | null>(null),
        ),
      )

    // Missing sdk.ts → nothing to patch.
    if (content === null) return
    // Already imports databases → nothing to do.
    if (content.includes(SDK_IMPORT_LINE)) return

    let patched = content

    // Add the import line after the first import.
    const firstImportEnd = patched.indexOf("\n")
    if (firstImportEnd !== -1) {
      patched =
        patched.slice(0, firstImportEnd + 1) +
        SDK_IMPORT_LINE +
        "\n" +
        patched.slice(firstImportEnd + 1)
    }

    // Replace the empty databases init with the imported one.
    if (patched.includes(SDK_OLD_INIT)) {
      patched = patched.replace(SDK_OLD_INIT, SDK_NEW_INIT)
    }

    yield* fs.writeFileString(sdkFilePath, patched)
  })
